
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface PsychologistWithAvailability {
  id: string;
  name: string;
  email: string;
  crp: string;
  bio: string;
  short_bio: string;
  specialties: string[];
  languages?: string[];
  price: number;
  rating: number;
  photo_url?: string;
  weekly_hours_minutes: number;
}

export const usePublicPsychologistsWithAvailability = () => {
  return useQuery({
    queryKey: ['psychologists-with-availability'],
    queryFn: async (): Promise<PsychologistWithAvailability[]> => {
      try {
        // Buscar psicólogos aprovados com disponibilidade
        const { data, error } = await supabase
          .from('psychologists')
          .select(`
            id,
            name,
            email,
            crp,
            bio,
            short_bio,
            specialties,
            languages,
            price,
            rating,
            photo_url,
            psychologist_availability!inner(
              start_time,
              end_time,
              duration
            )
          `)
          .eq('status', 'approved')
          .eq('is_approved', true)
          .eq('psychologist_availability.is_recurring', true);

        if (error) throw error;

        // Calcular horas semanais para cada psicólogo
        const psychologistsWithHours = data?.map(psych => {
          const totalMinutes = psych.psychologist_availability.reduce((total: number, avail: any) => {
            const start = avail.start_time.split(':').map(Number);
            const end = avail.end_time.split(':').map(Number);
            const startMinutes = start[0] * 60 + start[1];
            const endMinutes = end[0] * 60 + end[1];
            return total + (endMinutes - startMinutes);
          }, 0);

          return {
            id: psych.id,
            name: psych.name,
            email: psych.email || '',
            crp: psych.crp,
            bio: psych.bio,
            short_bio: psych.short_bio,
            specialties: psych.specialties || [],
            languages: psych.languages || [],
            price: psych.price,
            rating: psych.rating || 0,
            photo_url: psych.photo_url,
            weekly_hours_minutes: totalMinutes
          };
        }) || [];

        return psychologistsWithHours;
      } catch (error) {
        console.error('Error fetching psychologists with availability:', error);
        return [];
      }
    },
  });
};

// Adicionar a função ausente para o carrossel
export const useFeaturedPsychologistsWithAvailability = () => {
  return useQuery({
    queryKey: ['featured-psychologists-carousel-with-availability'],
    queryFn: async (): Promise<PsychologistWithAvailability[]> => {
      try {
        // Buscar psicólogos em destaque com disponibilidade
        const { data, error } = await supabase
          .from('psychologists')
          .select(`
            id,
            name,
            email,
            crp,
            bio,
            short_bio,
            specialties,
            languages,
            price,
            rating,
            photo_url,
            psychologist_availability!inner(
              start_time,
              end_time,
              duration
            )
          `)
          .eq('status', 'approved')
          .eq('is_approved', true)
          .eq('featured_in_carousel', true)
          .eq('psychologist_availability.is_recurring', true)
          .order('rating', { ascending: false })
          .limit(6);

        if (error) throw error;

        // Calcular horas semanais para cada psicólogo
        const psychologistsWithHours = data?.map(psych => {
          const totalMinutes = psych.psychologist_availability?.reduce((total: number, avail: any) => {
            const start = avail.start_time.split(':').map(Number);
            const end = avail.end_time.split(':').map(Number);
            const startMinutes = start[0] * 60 + start[1];
            const endMinutes = end[0] * 60 + end[1];
            return total + (endMinutes - startMinutes);
          }, 0) || 0;

          return {
            id: psych.id,
            name: psych.name,
            email: psych.email || '',
            crp: psych.crp,
            bio: psych.bio,
            short_bio: psych.short_bio,
            specialties: psych.specialties || [],
            languages: psych.languages || [],
            price: psych.price,
            rating: psych.rating || 0,
            photo_url: psych.photo_url,
            weekly_hours_minutes: totalMinutes
          };
        }) || [];

        return psychologistsWithHours;
      } catch (error) {
        console.error('Error fetching featured psychologists with availability:', error);
        return [];
      }
    },
  });
};
